<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Provider Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.provider_name')"
                v-model.trim="filterOptions.provider_name"
              />
              <!-- End:: Provider Name Input -->

              <!-- Start:: Brand Input -->
              <base-select-input
                col="4"
                :optionsList="brands"
                :placeholder="$t('PLACEHOLDERS.brand')"
                v-model="filterOptions.brand"
              />
              <!-- End:: Brand Input -->

              <!-- Start:: Model Input -->
              <base-select-input
                col="4"
                :optionsList="models"
                :placeholder="$t('PLACEHOLDERS.model')"
                v-model="filterOptions.model"
              />
              <!-- End:: Model Input -->

              <!-- Start:: License Plate Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.licensePlate')"
                v-model.trim="filterOptions.license_plate"
              />
              <!-- End:: License Plate Input -->

              <!-- Start:: Activation Status Input -->
              <base-select-input
                col="3"
                :optionsList="activation_statuses"
                :placeholder="$t('PLACEHOLDERS.activation_status')"
                v-model="filterOptions.activation_status"
              />
              <!-- End:: Activation Status Input -->

              <!-- Start:: From Date Input -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from')"
                v-model.trim="filterOptions.from"
              />

              <!-- Start:: To Date Input -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to')"
                v-model.trim="filterOptions.to"
              />
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.vehicles") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <template v-slot:[`item.provider_mobile`]="{ item }">
          {{ item.provider?.mobile }}
        </template>

        <template v-slot:[`item.vehicle_image`]="{ item }">
          <div class="table_image_wrapper">
            <h6
              class="text-danger"
              v-if="!item.vehicle_images || !item.vehicle_images.length"
            >
              {{ $t("TABLES.noData") }}
            </h6>
            <button
              class="my-1"
              @click="showImageModal(item.vehicle_images[0])"
              v-else
            >
              <img
                class="rounded"
                :src="item.vehicle_images[0]"
                width="60"
                height="60"
              />
            </button>
          </div>
        </template>

        <!-- Start:: Provider Name -->
        <template v-slot:[`item.provider_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.provider?.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.provider.name }}</h6>
        </template>

        <!-- Start:: Vehicle Info -->
        <template v-slot:[`item.vehicle_info`]="{ item }">
          <div>
            <p>
              <strong>{{ item.vehicle_model?.vehicle_brand?.name }}</strong>
            </p>
            <p>{{ item.vehicle_model?.name }}</p>
            <p>{{ item.model_year }}</p>
          </div>
        </template>

        <!-- Start:: License Plate -->
        <template v-slot:[`item.license_plate`]="{ item }">
          <h6 class="text-danger" v-if="!item.plate_number">
            {{ $t("TABLES.noData") }}
          </h6>
          <span class="license-plate" v-else>{{ item.plate_number }}</span>
        </template>

        <!-- Start:: Price -->
        <template v-slot:[`item.price`]="{ item }">
          <span>{{ item.price }} {{ item.currency }}</span>
        </template>

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" text-color="white" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Start:: Activation -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div class="activation" dir="ltr" style="z-index: 1">
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="toggleStatus(item)"
            ></v-switch>
          </div>
        </template>

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('vehicles show', 'vehicles')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('vehicles updateprice', 'vehicles')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('vehicles delete', 'vehicles')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.vehicle_model?.name || "هذا العنصر",
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>
                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Start:: Image Modal -->
          <image-modal
            v-if="dialogImage"
            :type="selectedItemType"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
        </template>
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllVehicles",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    activation_statuses() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.is_active"),
          value: "1",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.inactive"),
          value: "0",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        provider_name: null,
        brand: null,
        model: null,
        license_plate: null,
        activation_status: null,
        from: null,
        to: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.provider_name"),
          value: "provider.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.brand"),
          value: "vehicle_model.vehicle_brand.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.model"),
          value: "vehicle_model.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.vehicleType"),
          value: "vehicle_type.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.model_year"),
          value: "model_year",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.price"),
          value: "price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.is_active"),
          value: "is_active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      brands: [],
      models: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data

      // Start:: Dialogs
      dialogImage: false,
      selectedItemImage: null,
      selectedItemType: "image",
      dialogDelete: false,
      itemToDelete: null,
      // End:: Dialogs
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },
  methods: {
    async getBrands() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "vehicle-brands?is_active=1",
        });
        this.brands = res.data.data.data;
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    async getModels() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "vehicle-models?is_active=1",
        });
        this.models = res.data.data.data;
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    showImageModal(imageUrl) {
      this.dialogImage = true;
      this.selectedItemImage = imageUrl;
      this.selectedItemType = "image";
    },

    // Start:: Filter Methods
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/vehicles/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.provider_name = null;
      this.filterOptions.brand = null;
      this.filterOptions.model = null;
      this.filterOptions.license_plate = null;
      this.filterOptions.activation_status = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/vehicles/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    // Start:: Table Methods
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "vehicles",
          params: {
            page: this.paginations.current_page,
            provider_name: this.filterOptions.provider_name,
            vehicle_brand: this.filterOptions.brand?.id,
            vehicle_model: this.filterOptions.model?.id,
            plate_number: this.filterOptions.license_plate,
            is_active: this.filterOptions.activation_status?.value,
            from_date: this.filterOptions.from,
            to_date: this.filterOptions.to,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },

    // Start:: Status Methods
    getStatusColor(status) {
      switch (status) {
        case "pending_review":
          return "orange";
        case "published":
          return "green";
        case "rejected":
          return "red";
        default:
          return "grey";
      }
    },

    getStatusText(status) {
      switch (status) {
        case "pending_review":
          return this.$t("PLACEHOLDERS.pending");
        case "published":
          return this.$t("PLACEHOLDERS.published");
        case "rejected":
          return this.$t("PLACEHOLDERS.rejected");
        default:
          return this.$t("PLACEHOLDERS.unknown");
      }
    },

    // Start:: CRUD Methods
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `vehicles/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.tableRows = this.tableRows.filter((item) => {
          return item.id != this.itemToDelete.id;
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },

    async toggleStatus(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `vehicles/${item.id}/activate`,
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.changeActivation"));
      } catch (error) {
        this.setTableRows();
        this.$message.error(error.response.data.message);
      }
    },

    showItem(item) {
      this.$router.push({ path: `/vehicles/show/${item.id}` });
    },

    editItem(item) {
      this.$router.push({ path: `/vehicles/edit/${item.id}` });
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.getModels();
    this.getBrands();
    this.setTableRows();
  },
};
</script>

<style>
span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
}

.price-tag {
  background-color: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
}
</style>
